import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MessageCircle, Send, Star, StarOff, Zap, Search, Wifi, WifiOff } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildSessionWsProtocol, resolveBackendWsBaseUrl } from '../../lib/wsAuth';

export default function ChatSupportPage() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [search, setSearch] = useState('');
  const sendingRef = useRef(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const selectedRoomRef = useRef(null);
  const [favorites, setFavorites] = useState([]);
  const [filterMode, setFilterMode] = useState('all');
  const inputRef = useRef(null);
  const reconnectTimer = useRef(null);
  const shouldReconnectRef = useRef(true);

  // Keep selectedRoomRef in sync
  useEffect(() => { selectedRoomRef.current = selectedRoom; }, [selectedRoom]);

  // ---- WebSocket Connection ----
  const connectWS = useCallback(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;

    const wsBase = resolveBackendWsBaseUrl();
    const roomParam = selectedRoomRef.current ? `?room_id=${encodeURIComponent(selectedRoomRef.current.room_id)}` : '';
    const protocol = buildSessionWsProtocol(token);
    const ws = protocol
      ? new WebSocket(`${wsBase}/api/ws/chat/admin${roomParam}`, [protocol])
      : new WebSocket(`${wsBase}/api/ws/chat/admin${roomParam}`);

    ws.onopen = () => {
      setWsConnected(true);
      // Send ping every 25s to keep alive
      ws._pingInterval = setInterval(() => {
        if (ws.readyState === 1) ws.send(JSON.stringify({ type: 'ping' }));
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_player_message') {
          // Update rooms list instantly
          setRooms(prev => {
            const idx = prev.findIndex(r => r.room_id === data.room_id);
            if (idx >= 0) {
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                last_message: data.preview,
                last_message_at: data.message?.created_at,
                unread_count: (updated[idx].unread_count || 0) + 1,
              };
              // Move to top
              const [room] = updated.splice(idx, 1);
              return [room, ...updated];
            }
            // New room
            return [{
              room_id: data.room_id,
              user_name: data.user_name,
              last_message: data.preview,
              last_message_at: data.message?.created_at,
              unread_count: 1,
              user_tier: 'starter',
              user_xp: 0,
            }, ...prev];
          });

          // If this room is selected, add message
          if (selectedRoomRef.current?.room_id === data.room_id && data.message) {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
        }

        if (data.type === 'new_message' && data.message) {
          if (selectedRoomRef.current?.room_id === data.message.room_id) {
            setMessages(prev => {
              if (prev.some(m => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
          }
        }

        if (data.type === 'message_sent' && data.message) {
          setMessages(prev => {
            const withoutTemp = prev.filter(m => !m.id.startsWith('temp_'));
            if (withoutTemp.some(m => m.id === data.message.id)) return withoutTemp;
            return [...withoutTemp, data.message];
          });
        }
      } catch {}
    };

    ws.onclose = () => {
      setWsConnected(false);
      clearInterval(ws._pingInterval);
      if (shouldReconnectRef.current) {
        reconnectTimer.current = setTimeout(() => connectWS(), 2000);
      }
    };

    ws.onerror = () => { ws.close(); };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connectWS();
    return () => {
      shouldReconnectRef.current = false;
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWS]);

  // ---- Initial Data Load ----
  useEffect(() => {
    fetchRooms();
    fetchFavorites();
  }, []);

  // ---- Refresh rooms periodically as fallback ----
  useEffect(() => {
    const interval = setInterval(fetchRooms, wsConnected ? 30000 : 5000);
    return () => clearInterval(interval);
  }, [wsConnected]);

  // ---- Load messages when room selected ----
  useEffect(() => {
    if (selectedRoom) {
      fetchMessages(selectedRoom.room_id);
      // Join room via WS
      if (wsRef.current?.readyState === 1) {
        wsRef.current.send(JSON.stringify({ type: 'join_room', room_id: selectedRoom.room_id }));
      }
    }
  }, [selectedRoom?.room_id]);

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchRooms = async () => {
    try {
      const res = await api.get('/api/chat/admin/rooms');
      setRooms(res.data.rooms || []);
    } catch {} finally { setLoading(false); }
  };

  const fetchFavorites = async () => {
    try {
      const res = await api.get('/api/chat/admin/favorites');
      setFavorites(res.data.favorites || []);
    } catch {}
  };

  const toggleFavorite = async (e, playerId) => {
    e.stopPropagation();
    try {
      if (favorites.includes(playerId)) {
        await api.delete(`/api/chat/admin/favorites/${playerId}`);
        setFavorites(prev => prev.filter(id => id !== playerId));
      } else {
        await api.post(`/api/chat/admin/favorites/${playerId}`);
        setFavorites(prev => [...prev, playerId]);
      }
    } catch { toast.error('Failed to update favorites'); }
  };

  const fetchMessages = async (roomId) => {
    try {
      const res = await api.get(`/api/chat/admin/room/${roomId}`);
      setMessages(res.data.messages || []);
      // Clear unread on this room
      setRooms(prev => prev.map(r => r.room_id === roomId ? { ...r, unread_count: 0 } : r));
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedRoom || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);

    const msgText = input.trim();
    const tempId = `temp_${Date.now()}`;

    // Optimistic add
    setMessages(prev => [...prev, {
      id: tempId,
      sender_id: user?.id,
      sender_name: user?.username || 'Support',
      sender_role: 'admin',
      message: msgText,
      created_at: new Date().toISOString(),
    }]);
    setInput('');
    inputRef.current?.focus();

    // Try WebSocket first (fastest)
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        room_id: selectedRoom.room_id,
        message: msgText,
      }));
      sendingRef.current = false;
      setSending(false);
      return;
    }

    // Fallback to REST
    try {
      const res = await api.post('/api/chat/admin/send', {
        room_id: selectedRoom.room_id,
        message: msgText,
      });
      setMessages(prev => {
        const clean = prev.filter(m => m.id !== tempId);
        return [...clean, res.data];
      });
    } catch {
      toast.error('Failed to send');
      setMessages(prev => prev.filter(m => m.id !== tempId));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const filteredRooms = rooms.filter(r => {
    if (filterMode === 'favorites' && !favorites.includes(r.user_id)) return false;
    if (search && !r.user_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalUnread = rooms.reduce((s, r) => s + (r.unread_count || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="flex gap-3" style={{ height: 'calc(100vh - 160px)', minHeight: '400px' }} data-testid="chat-support-page">
      {/* Rooms Sidebar */}
      <div className="w-80 flex-shrink-0 glass-card rounded-xl overflow-hidden flex flex-col">
        <div className="p-3 border-b border-white/5 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
              Chats {totalUnread > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{totalUnread}</span>}
            </h3>
            <div className={`flex items-center gap-1 text-[10px] ${wsConnected ? 'text-green-400' : 'text-red-400'}`}>
              {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {wsConnected ? 'Live' : 'Reconnecting...'}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search players..."
              className="pl-8 h-8 text-xs bg-[#0B0E14] border-white/10"
              data-testid="chat-search"
            />
          </div>
          <div className="flex rounded-md bg-[#0B0E14] p-0.5">
            <button onClick={() => setFilterMode('all')} className={`flex-1 py-1 rounded text-[11px] font-medium ${filterMode === 'all' ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>All</button>
            <button onClick={() => setFilterMode('favorites')} className={`flex-1 py-1 rounded text-[11px] font-medium flex items-center justify-center gap-0.5 ${filterMode === 'favorites' ? 'bg-[#D4AF37] text-black' : 'text-gray-400'}`}>
              <Star className="w-3 h-3" /> Fav
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredRooms.map(room => (
            <button
              key={room.room_id}
              onClick={() => setSelectedRoom(room)}
              data-testid={`room-${room.room_id}`}
              className={`w-full p-3 text-left border-b border-white/5 hover:bg-white/5 transition-all ${
                selectedRoom?.room_id === room.room_id ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-0.5">
                <span className="font-medium text-sm truncate flex-1">{room.user_name || 'Anonymous'}</span>
                <div className="flex items-center gap-1.5">
                  {room.unread_count > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">{room.unread_count}</span>
                  )}
                  <div onClick={(e) => toggleFavorite(e, room.user_id)} className={`p-0.5 rounded hover:bg-white/10 cursor-pointer ${favorites.includes(room.user_id) ? 'text-[#D4AF37]' : 'text-gray-600'}`}>
                    {favorites.includes(room.user_id) ? <Star className="w-3 h-3 fill-current" /> : <StarOff className="w-3 h-3" />}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-gray-400 truncate">{room.last_message || 'No messages'}</p>
              <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500">
                <span className="px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded text-[9px] font-medium">{(room.user_tier || 'starter').toUpperCase()}</span>
                <span>{(room.user_xp || 0).toLocaleString()} XP</span>
                {room.last_message_at && <span>{timeAgo(room.last_message_at)}</span>}
              </div>
            </button>
          ))}
          {filteredRooms.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">{filterMode === 'favorites' ? 'No favorites' : search ? 'No matches' : 'No active chats yet'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 glass-card rounded-xl overflow-hidden flex flex-col">
        {selectedRoom ? (
          <>
            <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-sm">
                  {selectedRoom.user_name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <h4 className="font-semibold text-sm">{selectedRoom.user_name}</h4>
                  <p className="text-[11px] text-gray-400">{selectedRoom.user_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded font-medium">{(selectedRoom.user_tier || '').toUpperCase()}</span>
                <span className="flex items-center gap-1 text-[#D4AF37]"><Zap className="w-3.5 h-3.5" /> {(selectedRoom.user_xp || 0).toLocaleString()} XP</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="chat-messages">
              {messages.length === 0 && (
                <div className="text-center text-gray-500 py-8 text-sm">No messages yet</div>
              )}
              {messages.map((msg) => {
                const isAdmin = msg.sender_role !== 'player';
                return (
                  <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] ${
                      isAdmin
                        ? 'bg-[#D4AF37] text-black rounded-2xl rounded-br-sm'
                        : 'bg-[#2A2F3A] text-white rounded-2xl rounded-bl-sm'
                    } ${msg.id.startsWith('temp_') ? 'opacity-70' : ''}`}>
                      <div className="px-3.5 pt-2 pb-1">
                        <p className={`text-[10px] font-bold mb-0.5 ${isAdmin ? 'text-black/60' : 'text-[#D4AF37]'}`}>
                          {isAdmin ? 'Support' : msg.sender_name || 'Player'}
                        </p>
                        <p className="text-[13px] leading-relaxed">{msg.message}</p>
                        <p className={`text-[10px] mt-0.5 text-right ${isAdmin ? 'text-black/40' : 'text-gray-500'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {msg.id.startsWith('temp_') && ' Sending...'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="px-4 py-3 border-t border-white/5">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type a message..."
                  className="bg-[#0B0E14] border-white/10 h-10"
                  disabled={sending}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  className="bg-[#D4AF37] hover:bg-[#E5C158] text-black px-5 h-10"
                  data-testid="chat-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageCircle className="w-14 h-14 mx-auto mb-3 opacity-20" />
              <p className="text-base font-medium">Select a conversation</p>
              <p className="text-sm mt-1">Player messages appear here in real-time</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}
